import { observable, action, makeObservable, runInAction } from 'mobx';
import { RootStore } from './root';
import * as yup from 'yup';
import { WithNetworkConcurrency } from './with-network-concurrency';
import { Lang, LangRegEx } from '../utils';
import { HttpMethod } from '../types';
import { AppTarget } from '~types/app-selection';

export type ExternalProviderEnum = 'six12' | 'lift';

const MAX_LOYALTY_PROGRAMS_FETCH = 100;
const MAX_LOYALTY_CLUBS_FETCH = 100;

// The picker authors YYYY-MM-DD, but the API stores it as UTC midnight. The
// shared DatePicker parses that returned instant in local time, so Toronto
// displays the previous day. Keep response schedule dates at the calendar day
// the operator authored.
const toCalendarDate = (value: unknown): unknown =>
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)
        ? value.slice(0, 10)
        : value;

/** Stable handle for the singleton Road to Rewards campaign club. */
export const ROAD_TO_REWARDS_SLUG = 'road-to-rewards';

const YupLoyaltyClubTranslation = yup.object({
    language: yup.string<Lang>().required().matches(LangRegEx),
    name: yup.string().required(),
    subtitle: yup.string().nullable(),
    description: yup.string().nullable(),
    // Couche-Tard art; `ckImage` is the Circle K variant.
    image: yup.string().nullable(),
    ckImage: yup.string().nullable(),
    // Palette key backing a transparent club image; null when it needs none.
    backgroundColor: yup.string().nullable().optional(),
});

const YupTierRewardSchedule = yup.object({
    id: yup.number().required(),
    startDate: yup.string().nullable().defined(),
    endDate: yup.string().nullable().defined(),
});

// Per-language copy for a repeatablePurchaseGrant club's single tier reward.
// Every field is required in each language.
const YupTierRewardTranslation = yup.object({
    language: yup.string<Lang>().required().matches(LangRegEx),
    title: yup.string().required(),
    description: yup.string().required(),
    image: yup.string().required(),
    pushNotificationTitle: yup.string().required(),
    pushNotificationBody: yup.string().required(),
    completedTitle: yup.string().required(),
    completedBody: yup.string().required(),
});

// The club's single tier reward. externalTierId / rewardType / position are set
// by the backend; the CMS supplies the promotion, targets and copy.
export const YupTierRewardInput = yup.object({
    promotionId: yup.number().required(),
    requiredPurchases: yup.number().min(1).required(),
    grantValidityDays: yup.number().min(1).nullable().optional(),
    maxClaims: yup.number().min(1).nullable().optional(),
    translations: yup.array(YupTierRewardTranslation).min(1).required(),
});

export const YupLoyaltyClubUpdatePayload = yup.object({
    name: yup.string().required(),
    isActive: yup.boolean().required(),
    startDate: yup.string().required(),
    registrationStartDate: yup.string().required(),
    registrationEndDate: yup.string().required(),
    endDate: yup.string().required(),
    // Last day rewards can still be claimed after visits stop counting.
    // Null for every club without a grace period.
    graceEndDate: yup.string().nullable().optional(),
    // IANA zone the three dates above were authored in.
    timezone: yup.string().optional(),
    // Optional: six12 may not have assigned a promotion id yet.
    six12PromotionId: yup.string().nullable().optional(),
    // Optional linked Games & Contests external game (contest card on Club Details).
    externalGameId: yup.number().nullable().optional(),
    translations: yup.array(YupLoyaltyClubTranslation).min(1).required(),
    tierRewardSchedules: yup.array(YupTierRewardSchedule).optional(),
    provinces: yup.array(yup.number().required()).min(1).optional(),
    // tierReward is intentionally NOT validated here: it's only sent for
    // repeatablePurchaseGrant clubs (added to the type below), and validating
    // the always-present form value would block editing legacy club types.
    // The backend validates it thoroughly.
});

export const APP_TARGETS = ['QC', 'CA', 'All'] as const;

export const YupLoyaltyClubCreatePayload = yup.object({
    loyaltyProgramId: yup.number().required(),
    name: yup.string().required(),
    slug: yup.string().required(),
    appTarget: yup
        .string()
        .oneOf([...APP_TARGETS])
        .required(),
    externalProvider: yup.string<ExternalProviderEnum>().nullable().optional(),
    six12PromotionId: yup.string().nullable().optional(),
    externalGameId: yup.number().nullable().optional(),
    isActive: yup.boolean().required(),
    startDate: yup.string().required(),
    registrationStartDate: yup.string().required(),
    registrationEndDate: yup.string().required(),
    endDate: yup.string().required(),
    translations: yup.array(YupLoyaltyClubTranslation).min(1).required(),
    provinces: yup.array(yup.number().required()).min(1).required(),
    tierReward: YupTierRewardInput.required(),
});

export const YupLoyaltyClubTranslationResponse = yup.object({
    id: yup.number().required(),
    language: yup.string<Lang>().required().matches(LangRegEx),
    name: yup.string().required(),
    subtitle: yup.string().nullable(),
    description: yup.string().nullable(),
    image: yup.string().nullable(),
    ckImage: yup.string().nullable(),
    backgroundColor: yup.string().nullable(),
});

const YupTierRewardTranslationResponse = yup.object({
    language: yup.string<Lang>().required().matches(LangRegEx),
    title: yup.string().required(),
    description: yup.string().nullable(),
    image: yup.string().nullable(),
    ctaUrl: yup.string().nullable(),
    pushNotificationTitle: yup.string().nullable(),
    pushNotificationBody: yup.string().nullable(),
    completedTitle: yup.string().nullable(),
    completedBody: yup.string().nullable(),
});

const YupLoyaltyClubResponse = yup.object({
    id: yup.number().required(),
    loyaltyProgramId: yup.number().required(),
    name: yup.string().required(),
    // This schema also validates every row of the club list, so a single club
    // with a null or empty slug would break fetching all of them.
    slug: yup.string().nullable().default(''),
    active: yup.boolean().required(),
    appTarget: yup.string().nullable(),
    type: yup.string().default('repeatablePurchaseGrant'),
    externalProvider: yup.string<ExternalProviderEnum>().nullable(),
    six12PromotionId: yup.string().nullable(),
    externalGameId: yup.number().nullable(),
    startDate: yup.string().required(),
    registrationStartDate: yup.string().required(),
    registrationEndDate: yup.string().required(),
    endDate: yup.string().required(),
    graceEndDate: yup.string().nullable(),
    timezone: yup.string().default('America/Toronto'),
    // Branch.io links, generated backend-side on save. Read-only here.
    deepLink: yup.string().nullable(),
    ckDeepLink: yup.string().nullable(),
    translations: yup.array(YupLoyaltyClubTranslationResponse).default([]),
    provinces: yup
        .array(
            yup.object({
                id: yup.number().required(),
                provinceId: yup.number().required(),
                provinceName: yup.string().required(),
            })
        )
        .nullable(),
    tierRewards: yup
        .array(
            yup.object({
                id: yup.number().required(),
                rewardType: yup.string().required(),
                externalTierId: yup.string().required(),
                promotionId: yup.number().nullable(),
                promotionName: yup.string().nullable(),
                promotionImage: yup.string().nullable(),
                // Render order within the club, on one sequence shared by
                // every reward type, so a club's reusable coupons need not
                // start at 0.
                position: yup.number().default(0),
                startDate: yup.string().nullable().transform(toCalendarDate),
                endDate: yup.string().nullable().transform(toCalendarDate),
                requiredPurchases: yup.number().nullable(),
                grantValidityDays: yup.number().nullable(),
                maxClaims: yup.number().nullable(),
                translations: yup
                    .array(YupTierRewardTranslationResponse)
                    .default([]),
                // Present only on coupon_choice tiers; empty elsewhere.
                rewardOptions: yup
                    .array(
                        yup.object({
                            id: yup.number().required(),
                            promotionId: yup.number().required(),
                            promotionName: yup.string().nullable(),
                            position: yup.number().default(0),
                        })
                    )
                    .default([]),
            })
        )
        .default([]),
});

/**
 * One reusable coupon as the replace endpoint takes it. A row carrying an `id`
 * updates that coupon, a row without one creates it, and any existing coupon
 * left out of the array is deleted. `position` is never sent; the backend
 * assigns it from the array order. Membership here is what marks a coupon
 * unlimited redemption, so the flag is not part of the payload.
 */
export const YupReusableCouponInput = yup.object({
    id: yup.number().optional(),
    promotionId: yup.number().required(),
    startDate: yup.string().nullable().defined(),
    endDate: yup.string().nullable().defined(),
});

const YupReusableCouponResponse = yup.object({
    id: yup.number().required(),
    promotionId: yup.number().required(),
    // Required: the editor sorts rows by position to reseed itself, and a
    // defaulted 0 would silently pull every coupon to the top on the next save.
    position: yup.number().required(),
    startDate: yup.string().nullable().defined().transform(toCalendarDate),
    endDate: yup.string().nullable().defined().transform(toCalendarDate),
});

// Required rather than defaulted: the editor reseeds its rows from this list,
// so an absent body would look like a club that just lost every coupon, and
// the next save would delete them for real. An empty array is still accepted.
const YupReusableCouponListResponse = yup.object({
    coupons: yup.array(YupReusableCouponResponse).required(),
});

const YupLoyaltyClubList = yup.object({
    total: yup.number().required(),
    results: yup.array(YupLoyaltyClubResponse).default([]),
});

const YupPaginatedLoyaltyClubList = YupLoyaltyClubList.shape({
    page: yup.number().required(),
    perPage: yup.number().required(),
});

/**
 * A coupon offerable at a milestone. Stock is part of the contract because one
 * sheet line becomes one promotion per region, and the regional copies share
 * both name and UPC — remaining stock is the only thing that tells them apart
 * in the picker.
 */
const YupRewardOptionCoupon = yup.object({
    id: yup.number().required(),
    name: yup.string().nullable(),
    activationCode: yup.string().nullable(),
    provinceCodes: yup.array(yup.string().required()).default([]),
    quantity: yup.number().nullable(),
    claimedCount: yup.number().default(0),
    remaining: yup.number().nullable(),
    isForFuelTester: yup.boolean().default(false),
    // Null when the promotion has none. Field name settled by a controller
    // ruling (RNGR-2381 slice 01) so consumers would not have to guess it.
    image: yup.string().nullable().default(null),
});

const YupRewardOptionCouponList = yup.object({
    total: yup.number().required(),
    results: yup.array(YupRewardOptionCoupon).default([]),
});

const YupLoyaltyProgram = yup.object({
    id: yup.number().required(),
    name: yup.string().required(),
    active: yup.boolean().required(),
});

const YupLoyaltyProgramListResponse = yup.object({
    results: yup.array(YupLoyaltyProgram).default([]),
});

export type LoyaltyClub = yup.InferType<typeof YupLoyaltyClubResponse>;
export type LoyaltyClubTierReward = LoyaltyClub['tierRewards'][number];
export type LoyaltyClubUpdatePayload = yup.InferType<
    typeof YupLoyaltyClubUpdatePayload
> & {
    // Sent only for repeatablePurchaseGrant clubs; see schema note above.
    tierReward?: LoyaltyClubTierRewardInput;
};
export type LoyaltyClubCreatePayload = yup.InferType<
    typeof YupLoyaltyClubCreatePayload
>;
export type LoyaltyClubTierRewardInput = yup.InferType<
    typeof YupTierRewardInput
>;
export type PaginatedLoyaltyClubList = yup.InferType<
    typeof YupPaginatedLoyaltyClubList
>;
export type LoyaltyProgram = yup.InferType<typeof YupLoyaltyProgram>;
export type RewardOptionCoupon = yup.InferType<typeof YupRewardOptionCoupon>;
export type ReusableCouponInput = yup.InferType<typeof YupReusableCouponInput>;
export type ReusableCoupon = yup.InferType<typeof YupReusableCouponResponse>;

/** One milestone as the ladder endpoint takes it: ABSOLUTE visits, ordered options. */
export type TierLadderMilestonePayload = {
    visits: number;
    options: { promotionId: number }[];
};

export type LoyaltyClubSearchParams = {
    perPage?: number;
    page?: number;
    q?: string;
};

export type LoyaltyClubQuery = LoyaltyClubSearchParams & {
    appTarget?: AppTarget;
};

export const APP_TARGET_CONFIG: Record<
    string,
    { label: string; icon: string }
> = {
    QC: { label: 'Couche-Tard (QC)', icon: '/ct-logo-xl.svg' },
    CA: { label: 'Circle K (CA)', icon: '/circle-k-xl-logo.svg' },
};

export class LoyaltyClubStore extends WithNetworkConcurrency {
    @observable
    paginatedLoyaltyClubs: PaginatedLoyaltyClubList | null = null;
    @observable isFetchingPaginatedCount = 0;
    @observable loyaltyPrograms: LoyaltyProgram[] = [];
    @observable isFetchingLoyaltyPrograms = false;

    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        super();
        makeObservable(this);
        this.rootStore = rootStore;
    }

    @action
    setPaginatedLoyaltyClubs(
        this: LoyaltyClubStore,
        loyaltyClubs: PaginatedLoyaltyClubList
    ) {
        this.paginatedLoyaltyClubs = loyaltyClubs;
    }

    @action
    async fetchPaginatedLoyaltyClubs(
        this: LoyaltyClubStore,
        searchParams: LoyaltyClubQuery
    ) {
        const tag = this.getTag();
        this.isFetchingPaginatedCount++;
        try {
            const response = await this.rootStore.makeNetworkCall(
                {
                    method: HttpMethod.GET,
                    url: '/loyalty/clubs',
                    params: searchParams,
                },
                YupPaginatedLoyaltyClubList
            );

            if (!response.err && this.isLatestTag(tag)) {
                this.setPaginatedLoyaltyClubs(response.data);
            }
        } finally {
            runInAction(() => {
                this.isFetchingPaginatedCount--;
            });
        }
    }

    /**
     * The Road to Rewards campaign is a singleton club, so its configuration
     * page has no id in the URL. Resolve it by its stable slug rather than a
     * hardcoded id, which differs between dev, staging and production.
     * Returns null when the environment has no campaign club seeded.
     */
    async fetchRoadToRewardsClub(
        this: LoyaltyClubStore
    ): Promise<LoyaltyClub | null> {
        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: '/loyalty/clubs',
                params: { perPage: MAX_LOYALTY_CLUBS_FETCH },
            },
            YupPaginatedLoyaltyClubList
        );

        if (response.err) {
            throw new Error('Failed to fetch loyalty clubs');
        }

        const match = response.data.results.find(
            (club) => club.slug === ROAD_TO_REWARDS_SLUG
        );

        return match ? this.fetchLoyaltyClub(match.id) : null;
    }

    async fetchLoyaltyClub(
        this: LoyaltyClubStore,
        id: number
    ): Promise<LoyaltyClub> {
        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: `/loyalty/clubs/${id}`,
            },
            YupLoyaltyClubResponse,
            { stripUnknown: true }
        );

        if (!response.err) {
            return response.data;
        }
        throw new Error('Failed to fetch loyalty club');
    }

    /**
     * Replaces the club's whole milestone ladder. Separate from
     * `updateLoyaltyClub` because the ladder is its own transactional endpoint:
     * option rows and their claim counters must be written together.
     */
    @action
    async replaceTierLadder(
        this: LoyaltyClubStore,
        clubId: number,
        milestones: TierLadderMilestonePayload[]
    ): Promise<void> {
        // 204 No Content — no response body to validate.
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.PUT,
            url: `/loyalty/clubs/${clubId}/tiers`,
            data: { milestones },
        });

        if (response.err) {
            throw new Error('Failed to save the milestone ladder');
        }
    }

    /**
     * Replaces the club's whole reusable-coupon list. Separate from
     * `updateLoyaltyClub` because creating, reordering and deleting rows has to
     * be written together for the positions to stay contiguous.
     */
    @action
    async replaceReusableCoupons(
        this: LoyaltyClubStore,
        clubId: number,
        coupons: ReusableCouponInput[]
    ): Promise<ReusableCoupon[]> {
        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.PUT,
                url: `/loyalty/clubs/${clubId}/reusable-coupons`,
                data: { coupons },
            },
            YupReusableCouponListResponse,
            { stripUnknown: true }
        );

        if (response.err) {
            throw new Error('Failed to save the reusable coupons');
        }
        return response.data.coupons;
    }

    @action
    async fetchRewardOptionCoupons(
        this: LoyaltyClubStore
    ): Promise<RewardOptionCoupon[]> {
        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: '/loyalty/reward-option-coupons',
            },
            YupRewardOptionCouponList
        );

        if (response.err) {
            throw new Error('Failed to fetch reward option coupons');
        }
        return response.data.results;
    }

    @action
    async updateLoyaltyClub(
        this: LoyaltyClubStore,
        payload: LoyaltyClubUpdatePayload,
        id: number
    ): Promise<void> {
        // 204 No Content — no response body to validate.
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.PUT,
            url: `/loyalty/clubs/${id}`,
            data: payload,
        });

        if (response.err) {
            throw new Error('Failed to update loyalty club');
        }
    }

    @action
    async createLoyaltyClub(
        this: LoyaltyClubStore,
        payload: LoyaltyClubCreatePayload
    ): Promise<LoyaltyClub> {
        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.POST,
                url: '/loyalty/clubs',
                data: payload,
            },
            YupLoyaltyClubResponse,
            { stripUnknown: true }
        );

        if (response.err) {
            throw new Error('Failed to create loyalty club');
        }
        return response.data;
    }

    @action
    async fetchLoyaltyPrograms(this: LoyaltyClubStore) {
        if (this.isFetchingLoyaltyPrograms || this.loyaltyPrograms.length > 0) {
            return;
        }
        this.isFetchingLoyaltyPrograms = true;
        try {
            const response = await this.rootStore.makeNetworkCall(
                {
                    method: HttpMethod.GET,
                    url: '/loyalty/programs',
                    params: { perPage: MAX_LOYALTY_PROGRAMS_FETCH },
                },
                YupLoyaltyProgramListResponse,
                { stripUnknown: true }
            );

            if (!response.err) {
                runInAction(() => {
                    this.loyaltyPrograms = response.data.results;
                });
            }
        } finally {
            runInAction(() => {
                this.isFetchingLoyaltyPrograms = false;
            });
        }
    }
}
